import { useState, useEffect } from 'react';

const UNITS = { piece:'шт', liter:'л', gram:'кг' };

export default function ProductGrid({ products, categories, cart, onAdd, onAddDuplicate, activePromos = [], scanClear = 0 }) {
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  // Після скану штрих-коду (POSPage інкрементує scanClear) — чистимо поле пошуку,
  // щоб набраний сканером код не заважав наступному пошуку/скану.
  useEffect(() => { if (scanClear) setSearch(''); }, [scanClear]);

  // Фільтруємо тару — вона не повинна показуватись в POS
  const visibleProducts = products.filter(p => !p.is_container);

  // Товари, на які діє акція (за scope товар/категорія) — підсвічуємо 🎁
  const promoIds = new Set();
  for (const promo of activePromos) {
    if (promo.scope === 'product' && promo.scope_id) promoIds.add(promo.scope_id);
    else if (promo.scope === 'category' && promo.scope_id) {
      products.forEach(p => { if (p.category_id === promo.scope_id) promoIds.add(p.id); });
    }
  }

  const filtered = visibleProducts.filter(p => {
    const q = search.toLowerCase().trim();
    const matchSearch = !q ||
      p.name.toLowerCase().includes(q) ||
      (p.barcode && p.barcode.includes(q));
    const matchCat = activeCat === 'all' || p.category_id === activeCat;
    return matchSearch && matchCat;
  });

  function renderCatIcon(photo_url) {
    if (!photo_url) return '📦';
    if (photo_url.startsWith('http')) return (
      <img src={photo_url} alt="" style={{width:'28px',height:'28px',objectFit:'cover',borderRadius:'6px'}} />
    );
    return photo_url;
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.top}>
        <input
          style={styles.search}
          placeholder="🔍 Назва або штрихкод..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
        />
      </div>

      {/* Категорії */}
      {categories.length > 0 && (
        <div style={styles.catPanel}>
          <div
            style={{...styles.catBtn, ...(activeCat==='all' ? styles.catBtnActive : {})}}
            onClick={() => setActiveCat('all')}>
            <span style={styles.catIcon}>🏪</span>
            <span style={styles.catLabel}>Всі</span>
          </div>
          {categories.filter(c => c.show_in_pos !== false).map(cat => (
            <div key={cat.id}
              style={{...styles.catBtn, ...(activeCat===cat.id ? styles.catBtnActive : {})}}
              onClick={() => setActiveCat(cat.id)}>
              <span style={styles.catIcon}>{renderCatIcon(cat.photo_url)}</span>
              <span style={styles.catLabel}>{cat.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Сітка товарів */}
      <div style={styles.grid}>
        {filtered.length === 0 ? (
          <div style={styles.empty}>
            {activeCat !== 'all' ? 'В цій категорії немає товарів' : 'Товарів не знайдено'}
          </div>
        ) : filtered.map(p => {
          const cartItems = cart.filter(i => i.product_id === p.id && !i.is_container_line);
          const inCart = cartItems.length > 0;
          const out = parseFloat(p.stock_quantity) === 0;
          const totalQty = cartItems.reduce((s, i) => s + i.quantity, 0);
          const hasContainers = p.unit === 'liter' || p.unit === 'gram';

          return (
            <div
              key={p.id}
              style={{
                ...styles.card,
                ...(out ? styles.cardOut : {}),
                ...(inCart ? styles.cardActive : {}),
              }}
              onClick={() => !out && onAdd(p)}
            >
              {/* Акція на товар */}
              {promoIds.has(p.id) && (
                <div style={styles.promoBadge} title="Діє акція">🎁</div>
              )}

              {/* Індикатор в кошику */}
              {inCart && (
                <div style={styles.cartBadge}>
                  {totalQty} {UNITS[p.unit]}
                </div>
              )}

              <div style={styles.cardName}>{p.name}</div>

              <div style={styles.cardPrice}>
                {parseFloat(p.price).toFixed(2)} ₴
                <span style={styles.cardUnit}>/{UNITS[p.unit]}</span>
              </div>

              <div style={styles.cardBottom}>
                {out ? (
                  <span style={styles.outLabel}>🔴 Немає</span>
                ) : (
                  <span style={styles.stock}>
                    {parseFloat(p.stock_quantity).toFixed(p.unit==='piece'?0:p.unit==='gram'?3:1)} {UNITS[p.unit]}
                  </span>
                )}
                {hasContainers && !out && (
                  <span style={styles.containerHint}>📦</span>
                )}
              </div>


            </div>
          );
        })}
      </div>
    </div>
  );
}


const styles = {
  wrap: { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
  top: { padding:'10px 12px', borderBottom:'1px solid #38301f', flexShrink:0 },
  search: { width:'100%', padding:'9px 12px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'14px', boxSizing:'border-box' },
  catPanel: { display:'flex', flexWrap:'wrap', gap:'0', borderBottom:'1px solid #38301f', flexShrink:0 },
  catBtn: { display:'flex', flexDirection:'column', alignItems:'center', padding:'10px 16px', cursor:'pointer', borderRight:'1px solid #38301f', minWidth:'72px', flexShrink:0, gap:'4px' },
  catBtnActive: { background:'#1c1812', borderBottom:'2px solid #f5a623' },
  catIcon: { fontSize:'24px', lineHeight:1, display:'flex', alignItems:'center', justifyContent:'center', height:'28px' },
  catLabel: { fontSize:'11px', color:'#9a8a6e', textAlign:'center', lineHeight:'1.2', maxWidth:'64px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  grid: { flex:1, overflowY:'auto', padding:'10px 12px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:'10px', alignContent:'start' },
  card: {
    background:'#211c14', border:'2px solid #38301f', borderRadius:'12px',
    padding:'14px 12px', cursor:'pointer', position:'relative',
    transition:'border-color 0.15s, background 0.15s',
    userSelect:'none',
  },
  cardActive: { border:'2px solid #f5a623', background:'rgba(245,166,35,.10)' },
  cardOut: { opacity:0.4, cursor:'not-allowed' },
  cartBadge: {
    position:'absolute', top:'-8px', right:'-8px',
    background:'#f5a623', color:'#14110c',
    borderRadius:'12px', padding:'2px 8px',
    fontSize:'12px', fontWeight:'bold',
    minWidth:'28px', textAlign:'center',
  },
  promoBadge: {
    position:'absolute', top:'-8px', left:'-8px',
    background:'#2d6a4f', borderRadius:'50%',
    width:'24px', height:'24px',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:'13px', boxShadow:'0 2px 6px rgba(0,0,0,.4)',
  },
  cardName: { fontSize:'14px', fontWeight:'bold', marginBottom:'8px', lineHeight:'1.3', minHeight:'36px' },
  cardPrice: { fontSize:'18px', color:'#f5a623', fontWeight:'bold', marginBottom:'6px' },
  cardUnit: { fontSize:'12px', color:'#9a8a6e', fontWeight:'normal' },
  cardBottom: { display:'flex', justifyContent:'space-between', alignItems:'center' },
  stock: { color:'#9a8a6e', fontSize:'12px' },
  outLabel: { color:'#ff6b6b', fontSize:'12px' },
  containerHint: { fontSize:'14px', opacity:0.6 },
  dupBtn: {
    width:'100%', marginTop:'8px', padding:'5px',
    background:'#38301f', color:'#6cb6ff',
    border:'none', borderRadius:'6px',
    cursor:'pointer', fontSize:'12px',
  },
};
